import {Component, Input} from '@angular/core';
import {SubscriptionPlanInfoModel} from "../../models/subscriptionPlanInfo.model";
import {AccountType} from "../../enums/account-type";
import {SubscriptionType} from "../../enums/subscription-type";
import {Language} from "../../models/language";
import {TranslateService} from "@ngx-translate/core";
import {Router} from '@angular/router';
import {UserService} from "../../service/user.service";
import {SharedConstants} from "../../utilityClass/sharedConstants";
import {SubscriptionService} from "../../service/subscription.service";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {ENGLISH_ROUTES_FOR_PLACEHOLDER, SUBSCRIPTION_ALREADY_EXISTED_MESSAGE} from "../../constants/global.constants";
import {DOLLAR_TO_DRAM_CURRENCY} from "../../constants/currency.constant";

@Component({
  selector: 'app-subscription-plan-card',
  templateUrl: './subscription-plan-card.component.html',
  styleUrls: ['./subscription-plan-card.component.scss', './subscription-plan-card-media.component.scss']
})
export class SubscriptionPlanCardComponent {

  @Input() subscriptionPlanInfoModel!: SubscriptionPlanInfoModel;
  @Input() isFromUserPage!: boolean;
  @Input() activeSubscriptionPlanId!: number;
  @Input() activeTeamSubscriptionPlanId!: number | undefined;
  @Input() isDisabledByMissingPayment: boolean = false;

  protected readonly FREE: string = 'subscription_plan_content.free';
  protected readonly MONTHLY: string = 'subscription_plan_content.monthly';
  protected readonly YEARLY: string = 'subscription_plan_content.yearly';
  protected readonly AMD: string = 'subscription_plan_content.AMD';
  protected readonly DOLLAR_TO_DRAM_CURRENCY = DOLLAR_TO_DRAM_CURRENCY;

  protected readonly AccountType = AccountType;
  protected readonly SubscriptionType = SubscriptionType;
  protected readonly Language = Language;

  protected currentLang: string = '';

  protected isAuthenticated: boolean = false;

  constructor(
    protected translate: TranslateService,
    private router: Router,
    private userService: UserService,
    private subscriptionService: SubscriptionService,
    private snackBar: SnackBarUtil,
  ) {
    this.currentLang = this.translate.currentLang || Language.HY;
    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
    });
    this.userService.currentUser.subscribe(user => {
      this.isAuthenticated = !!user;
    });
  }

  protected payOrLogin(subscriptionAccountType: AccountType) {
    const price = this.subscriptionPlanInfoModel.subscriptionPlanData.price;
    const subscriptionId = this.subscriptionPlanInfoModel.subscriptionPlanData.id;
    const isMonthly = !this.subscriptionPlanInfoModel.isYearly;
    
    if (!this.userService.token || this.userService.token == '' || price == 0) {
      this.router.navigate(['/login'], {
        state: {
          fromSubscriptionPage: true,
          subscriptionInfoId: subscriptionId,
          isMonthly: isMonthly
        }
      });
      return;
    }

    this.router.navigate([`subscriptions/payment/${subscriptionId}/${isMonthly}`]);
  }

  getDescription(): string {
    this.translate.use(this.currentLang);
    return this.subscriptionPlanInfoModel.subscriptionPlanData.description;
  }

  getTranslatedSubscriptionTitle(subscriptionType: SubscriptionType): string {
    const translationKey = `subscription_plans.${subscriptionType.toLowerCase()}`;
    return this.translate.instant(translationKey);
  }

  checkActiveSubscriptionPlan(subscriptionAccountType: AccountType, subscriptionInfoId: number, isMonthly: boolean) {
    this.subscriptionService.checkActiveSubscription(subscriptionAccountType).subscribe({
      next: () => {
        this.router.navigate([`subscriptions/payment/${subscriptionInfoId}/${isMonthly}`]);
      },
      error: (error) => {
        if (error.status === 409) {
          this.showSnackbar(
            MockupDataModel.error(this.translate.instant('subscription_already_existed_message')),
            SnackBarUtil.CLASSES.error
          );
        }
      }
    });
  }

  checkUserProfileRouteLanguage(): boolean {
    return ENGLISH_ROUTES_FOR_PLACEHOLDER.some(route => this.router.url.includes(route));
  }

  checkIfActiveSubscriptionPlan(): boolean {
    return this.subscriptionPlanInfoModel.subscriptionPlanData.id === this.activeSubscriptionPlanId ||
      this.subscriptionPlanInfoModel.subscriptionPlanData.id === this.activeTeamSubscriptionPlanId
  }

  isCardDisabled(): boolean {
    return this.checkIfActiveSubscriptionPlan() || this.isDisabledByMissingPayment;
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  protected readonly SharedConstants = SharedConstants;
}
